import React, { FormEvent, useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import { useRouter } from "next/router";
import { SubmitButtonWrapper, FormButton } from "@/lib/styles";
import InputFile from "./InputFile";
import SelectDropdown from "./SelectDropdown";
import InputCheckboxWrapper from "./InputCheckboxWrapper";
import TextArea from "./TextArea";
import InputText from "./InputText";
import { z } from "zod";
import { FormElement, Subtitle, Title } from "./styles";
import { EJ_PUBLIC_KEY, EJ_SERVICE_ID } from "@/lib/constants";
import { InputDataSchema } from "@/lib/ms-dynamics-365/lead";
import { useTranslation } from "@/lib/translations";
import InputCountry from "./InputCountry";

export default function Form(props: any) {
  const formRef = useRef<HTMLFormElement>(null);
  const router = useRouter();
  const { locale = "it" } = router;
  const t = {
    loadingText:
      useTranslation("contactPage.loadingButtonSubmit", locale) || "Loading",
  };
  const {
    form: {
      buttonText = "Submit",
      fields = [],
      shortText,
      heading,
      templateId,
      sendTo,
      validErrorText = "Is Required",
      sendToCRM,
      redirectonSuccess,
    },
    wishListJsonStringify = null,
    hideTitle = false,
  } = props;
  const [submitButtonText, setSubmitButtonText] = useState<string>(buttonText);

  async function getMSDynamic365AccessToken() {
    const response = await fetch("/api/ms-dynamics-365/auth");
    const data = await response.json();
    return data.access_token;
  }

  async function createMSDynamic365Lead(
    token: InputDataSchema["token"],
    lead: InputDataSchema["lead"]
  ) {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      token,
      lead,
    });

    const requestOptions = {
      method: "POST",
      header: myHeaders,
      body: raw,
    };

    const response = await fetch("/api/ms-dynamics-365/lead", requestOptions);

    // Microsoft Dynamic 365 uses status codes to signal to clients if
    // a post request was successful or not.
    if (!response.ok) throw new Error("Something went wrong");
  }

  const createLeadInCRM = () => {
    return new Promise(async (resolve, reject) => {
      if (!sendToCRM) resolve(true);

      try {
        const accessToken = await getMSDynamic365AccessToken();
        const formData = new FormData(formRef.current || undefined);
        const formObj = Object.fromEntries(formData.entries());

        const sdescription = `PRODUCT: ${formObj["product"] || ""}, QUANTITY: ${
          formObj["quantity"] || ""
        } \n${formObj["message"] || ""}`;
        let description = formObj["message"]?.toString() || "";

        if (formObj["product"]) {
          description += `\nPRODUCT: ${formObj["product"] || ""}, ${
            formObj["quantity"] || ""
          }`;
        }

        if (formObj["wishList"]) {
          description += `\n${formObj["wishList"] || ""}`;
        }

        const contactBy = formObj["contact_by"] === "Phone" ? "3" : "5";
        const countryISO = formObj["country"]?.toString() || "";

        const lead = {
          leadsourcecode: "806350001",
          firstname: formObj["firstname"]?.toString() || "",
          lastname: formObj["lastname"]?.toString() || "",
          companyname: formObj["company"]?.toString() || "",
          address1_country: countryISO,
          "var_countryid@odata.bind": `/var_countries(var_code='${countryISO}')`,
          address1_stateorprovince: formObj["region"]?.toString() || "",
          address1_city: formObj["city"]?.toString() || "",
          address1_line1: formObj["address"]?.toString() || "",
          emailaddress1: formObj["email"]?.toString() || "",
          telephone1: formObj["tel"]?.toString() || "",
          preferredcontactmethodcode: contactBy,
          description: description,
          Lead_Annotation: [
            {
              isdocument: false,
              documentbody: "",
              filename: "",
            },
          ],
          msdyn_gdproptout: formObj["legal1"] === undefined,
          donotemail: formObj["legal2"] === undefined,
          donotphone: formObj["legal2"] === undefined,
          donotpostalmail: formObj["legal2"] === undefined,
          donotbulkemail: formObj["legal3"] === undefined,
        };

        if (formObj["fileUpload"] instanceof File) {
          const fileText = await formObj["fileUpload"].text();
          lead["Lead_Annotation"][0]["isdocument"] = true;
          lead["Lead_Annotation"][0]["documentbody"] = btoa(
            unescape(encodeURIComponent(fileText))
          );
          lead["Lead_Annotation"][0]["filename"] = formObj["fileUpload"].name;
        }

        await createMSDynamic365Lead(accessToken, lead);
        resolve(true);
      } catch (err) {
        reject(err);
      }
    });
  };

  const sendToEmailjs = () => {
    return new Promise((resolve, reject) => {
      if (!templateId) resolve(true);

      emailjs
        .sendForm(
          EJ_SERVICE_ID,
          templateId,
          formRef.current as any,
          EJ_PUBLIC_KEY
        )
        .then(
          () => {
            resolve(true);
          },
          (err) => {
            reject(new Error(err.text));
          }
        );
    });
  };

  const validForm = () => {
    const fields = formRef.current?.querySelectorAll("[required]");
    const invalid: any[] = [];

    if (!fields) {
      return true;
    }

    fields.forEach((field: any) => {
      const value = field.value;

      if (field.type === "email") {
        const emailSchema = z.string().email();
        const validation = emailSchema.safeParse(value);
        if (validation.success == false) {
          field.parentElement?.classList.add("error");
          invalid.push(field);
        }
      } else if (field.type === "text") {
        const textSchema = z.string().trim().min(1);

        textSchema.safeParse(value);
        const validation = textSchema.safeParse(value);
        if (validation.success == false) {
          field.parentElement?.classList.add("error");
          invalid.push(field);
        }
      } else if (field.type === "checkbox") {
        if (field.checked === false) {
          field.parentElement?.classList.add("error");
          invalid.push(field);
        }
      }
    });

    if (invalid.length > 0) {
      window.scrollTo(0, 0);
      return false;
    }

    return true;
  };

  const handleLoadingSubmitButton = (status: boolean) => {
    const submitButton = formRef.current?.querySelector("[type='submit']");
    if (status) {
      setSubmitButtonText(t.loadingText);
      submitButton?.setAttribute("disabled", "true");
    } else {
      setSubmitButtonText(buttonText);
      submitButton?.removeAttribute("disabled");
    }
  };

  const handleSubmit = (e: FormEvent) => {
    handleLoadingSubmitButton(true);

    e.preventDefault();
    if (!validForm()) {
      handleLoadingSubmitButton(false);
      return;
    }

    Promise.all([createLeadInCRM(), sendToEmailjs()])
      .then((values) => {
        formRef?.current?.reset();
        router.push(redirectonSuccess ?? "/contacts?success=true");
      })
      .catch((err) => {
        if (err instanceof Error) {
          console.error(err.message);
          alert("Error submitting form");
        }
      });
  };

  return (
    <>
      {!hideTitle && (
        <>
          {heading && <Title as={"h1"}>{heading}</Title>}
          {shortText && <Subtitle>{shortText}</Subtitle>}
        </>
      )}
      <FormElement
        onSubmit={handleSubmit}
        ref={formRef}
        encType="multipart/form-data"
        method="post"
        noValidate
      >
        {fields &&
          fields.map((data: any) => {
            if (!data._key) return null;
            switch (data._type) {
              case "inputText":
                return (
                  <InputText
                    key={data._key}
                    {...data}
                    validErrorText={validErrorText}
                  />
                );
              case "inputCountry":
                return (
                  <InputCountry
                    key={data._key}
                    {...data}
                    validErrorText={validErrorText}
                  />
                );
              case "inputTextArea":
                return <TextArea key={data._key} {...data} />;
              case "inputCheckbox":
                return <InputCheckboxWrapper key={data._key} {...data} />;
              case "inputFileUpload":
                return <InputFile key={data._key} {...data} />;
              case "dropdown":
                return (
                  <SelectDropdown
                    key={data._key}
                    {...data}
                    validErrorText={validErrorText}
                  />
                );
              default:
                return null;
            }
          })}

        <input
          readOnly
          type="text"
          style={{
            width: "0",
            height: "0",
            opacity: "0",
            visibility: "hidden",
          }}
          value={sendTo}
          name="send_to"
        ></input>

        {wishListJsonStringify && (
          <input
            readOnly
            type="text"
            style={{
              width: "0",
              height: "0",
              opacity: "0",
              visibility: "hidden",
            }}
            value={wishListJsonStringify}
            name="wishList"
          ></input>
        )}
        <SubmitButtonWrapper>
          <FormButton type="submit">{submitButtonText}</FormButton>
        </SubmitButtonWrapper>
      </FormElement>
    </>
  );
}
